import React, { useEffect, useState } from 'react';
import { HUB_BACKDROP } from '../../content/hubLanding';

/** Rough slot width for hub grid (1 / 2 / 3 columns + scaled layout). */
const CARD_SIZES = '(max-width: 767px) 92vw, (max-width: 1023px) 48vw, 40vw';

const backdropImgClass = 'h-full min-h-full w-full object-cover object-top';

function usePrefersReducedMotion() {
  const [reduce, setReduce] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduce(mq.matches);
    const onChange = () => setReduce(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return reduce;
}

function HubBackdropStill({ fetchPriority = 'high' }) {
  return (
    <picture className="pointer-events-none block h-full min-h-full w-full">
      <source media="(max-width: 1024px)" srcSet={HUB_BACKDROP.webp1280} type="image/webp" />
      <source media="(min-width: 1025px)" srcSet={HUB_BACKDROP.webp3480} type="image/webp" />
      <img
        src={HUB_BACKDROP.png}
        alt=""
        className={backdropImgClass}
        aria-hidden
        fetchPriority={fetchPriority}
        decoding="async"
      />
    </picture>
  );
}

/** Full-bleed looping video backdrop; static art only when `prefers-reduced-motion`. */
export function HubLandingBackdrop() {
  const prefersReducedMotion = usePrefersReducedMotion();

  if (prefersReducedMotion) {
    return <HubBackdropStill />;
  }

  return (
    <div className="pointer-events-none block h-full min-h-full w-full">
      <video
        className={backdropImgClass}
        autoPlay
        muted
        loop
        playsInline
        poster={HUB_BACKDROP.poster}
        preload="auto"
        aria-hidden
      >
        <source media="(max-width: 1024px)" src={HUB_BACKDROP.videoMp41280} type="video/mp4" />
        <source src={HUB_BACKDROP.videoMp41920} type="video/mp4" />
      </video>
    </div>
  );
}

/**
 * @param {object} props
 * @param {string} props.fallback PNG path (WebP fallback / legacy browsers)
 * @param {string} props.webpSrcSet comma-separated srcset for WebP widths
 * @param {string} [props.alt]
 */
export function HubCardThumb({ fallback, webpSrcSet, alt = '' }) {
  return (
    <picture className="block h-full w-full">
      <source type="image/webp" srcSet={webpSrcSet} sizes={CARD_SIZES} />
      <img src={fallback} alt={alt} className="h-full w-full object-cover" loading="lazy" decoding="async" />
    </picture>
  );
}
